/**
 * Which engagement a timesheet entry belongs to — the one definition of the
 * rule, shared by the API (write validation + submit) and the web grid (what
 * the picker offers and when it appears at all).
 *
 * An entry stores the stable `engagements.id`; the SOW version in force on the
 * entry's date is a detail of resolution, not of attribution.
 *
 * The rule, in order:
 *
 *   1. No activity on the row → nothing is resolved and nothing is asked.
 *      There is nothing to match an engagement against yet, and binding on
 *      the customer alone would produce a pick a later activity could
 *      invalidate.
 *   2. Count the engagements whose term covers the entry's date AND which
 *      match the activity. An engagement matches when it lists that
 *      activity — whatever its billing model — or when it lists NO
 *      activities at all, which is a wildcard that matches everything.
 *   3. Act on the count: 0 → save unattributed, 1 → bind silently,
 *      2+ → ask the user, and refuse the submit until they answer.
 *
 * Only the 2+ case is a question. Everything else resolves in silence.
 */

import type { BillingModel } from './sows';
import { coversDate, type HalfOpenDateRange } from './temporal';

/**
 * One covering VERSION of an engagement, as the coverage query returns it.
 * Version-grained on purpose: a week window can span an amendment that
 * changes the activity list, so the date filter has to run per version
 * rather than over a pre-flattened engagement.
 */
export interface EngagementVersion extends HalfOpenDateRange {
  customerId: string;
  /** Stable engagement id — what lands in `timesheets.engagement_id`. */
  engagementId: string;
  version: number;
  name: string;
  documentNo: string | null;
  /**
   * Retainer and metrics lists are a SCOPE as well as an attribution rule; a
   * T&M list is attribution only — under T&M every hour is billable at its
   * role's rate, so nothing is in or out of scope. See `coversActivity`.
   */
  billingModel: BillingModel;
  /**
   * Activity (product) ids this engagement version lists. Retainer, T&M and
   * metrics engagements all scope activities the same way.
   *
   * EMPTY IS MEANINGFUL, not missing data: a version that lists no activities
   * matches every activity — a T&M or retainer written without itemising the
   * work it covers.
   */
  activityIds: readonly string[];
}

/** A logical engagement the picker can offer, or bind to, for one entry. */
export interface EngagementCandidate {
  engagementId: string;
  name: string;
  documentNo: string | null;
  billingModel: BillingModel;
  /** The version that matched, for the caller that needs the term text. */
  effectiveFrom: string;
  effectiveTo: string | null;
  /** True when the match came from an empty activity list, not a listed
   *  activity — the picker labels these so a wildcard match is legible. */
  matchedByWildcard: boolean;
  /**
   * Whether this engagement actually covers the entry's activity — listed, or
   * by an empty (wildcard) list. False only for one thing: a retainer or
   * metrics engagement the user deliberately attributed time to that does not
   * list the activity, which is what "out of scope" means and is a legitimate
   * recording. ALWAYS true for T&M, which has no scope to be outside of.
   */
  coversActivity: boolean;
}

/**
 * What the UI should do about this entry's engagement.
 *
 * `pending` is gate 1 (no activity yet) and is NOT the same as `none`: one
 * says "not resolvable yet", the other says "resolved, and the answer is that
 * nothing covers this". Collapsing them would make an unfilled row look like
 * a customer with no live engagement.
 */
export type EngagementResolution =
  | { kind: 'pending' }
  | { kind: 'none' }
  | { kind: 'bound'; engagement: EngagementCandidate }
  | { kind: 'ask'; candidates: EngagementCandidate[] };

/** Does this version cover the activity? Empty list = wildcard = always. */
export function engagementVersionMatchesActivity(
  version: Pick<EngagementVersion, 'activityIds'>,
  activityId: string,
): boolean {
  return version.activityIds.length === 0 || version.activityIds.includes(activityId);
}

/**
 * The engagements covering `(customerId, date)` that match `activityId`,
 * deduped to logical engagements and ordered by name for a stable picker.
 *
 * Dedupe is a safety net rather than a real merge: `sows_no_overlap` forbids
 * two versions of one engagement covering the same date, so at most one
 * version per engagement can survive the date filter. The first wins if that
 * constraint is ever relaxed.
 */
export function resolveEngagementCandidates(
  versions: readonly EngagementVersion[],
  opts: { customerId: string; date: string; activityId: string },
): EngagementCandidate[] {
  const byEngagement = new Map<string, EngagementCandidate>();
  for (const v of versions) {
    if (v.customerId !== opts.customerId) continue;
    if (!coversDate(v, opts.date)) continue;
    if (!engagementVersionMatchesActivity(v, opts.activityId)) continue;
    if (byEngagement.has(v.engagementId)) continue;
    byEngagement.set(v.engagementId, toCandidate(v, true));
  }
  return [...byEngagement.values()].sort(
    (a, b) => a.name.localeCompare(b.name) || (a.engagementId < b.engagementId ? -1 : 1),
  );
}

/** One version → the picker's view of its engagement. */
function toCandidate(v: EngagementVersion, coversActivity: boolean): EngagementCandidate {
  return {
    engagementId: v.engagementId,
    name: v.name,
    documentNo: v.documentNo,
    billingModel: v.billingModel,
    effectiveFrom: v.effectiveFrom,
    effectiveTo: v.effectiveTo,
    matchedByWildcard: v.activityIds.length === 0,
    coversActivity,
  };
}

/**
 * Every engagement active for `(customerId, date)`, whether or not it covers
 * any particular activity.
 *
 * This is the OVERRIDE set. Attributing time to an engagement that does not
 * list the activity is a legitimate recording — the write guard allows exactly
 * it — so the picker offers this alongside the matching ones, and a stored
 * value is resolved against it (see `resolveEngagementForEntry`).
 *
 * `coversActivity` is left true here because this function is not asked about
 * an activity; callers that know one re-derive it.
 */
export function engagementsActiveOn(
  versions: readonly EngagementVersion[],
  opts: { customerId: string; date: string },
): EngagementCandidate[] {
  const byEngagement = new Map<string, EngagementCandidate>();
  for (const v of versions) {
    if (v.customerId !== opts.customerId) continue;
    if (!coversDate(v, opts.date)) continue;
    if (byEngagement.has(v.engagementId)) continue;
    byEngagement.set(v.engagementId, toCandidate(v, true));
  }
  return [...byEngagement.values()].sort(
    (a, b) => a.name.localeCompare(b.name) || (a.engagementId < b.engagementId ? -1 : 1),
  );
}

/**
 * The full rule for one entry: gate on the activity, then act on the count.
 *
 * `currentEngagementId` is what the row already carries. A stored engagement
 * that is still a candidate keeps the row `bound` — so re-opening a week
 * never re-asks a question the user already answered — while one that is no
 * longer a candidate (the activity changed under it) is dropped, putting the
 * row back to `ask`.
 */
export function resolveEngagementForEntry(
  versions: readonly EngagementVersion[],
  entry: {
    customerId: string | null;
    date: string;
    activityId: string | null;
    currentEngagementId?: string | null;
  },
): EngagementResolution {
  if (entry.customerId == null || entry.activityId == null) return { kind: 'pending' };
  const candidates = resolveEngagementCandidates(versions, {
    customerId: entry.customerId,
    date: entry.date,
    activityId: entry.activityId,
  });
  // A STORED engagement always wins, and is looked up in the full active set
  // rather than only the matching one. Two things depend on this. A week the
  // user already answered never re-asks. And an out-of-scope attribution — an
  // engagement that does not list the activity, which the picker offers and
  // the server accepts — stays visible instead of reading as "not covered",
  // which would leave the user re-picking a choice they had already made.
  if (entry.currentEngagementId != null) {
    const stored =
      candidates.find((c) => c.engagementId === entry.currentEngagementId) ??
      engagementsActiveOn(versions, { customerId: entry.customerId, date: entry.date }).find(
        (c) => c.engagementId === entry.currentEngagementId,
      );
    if (stored) {
      return {
        kind: 'bound',
        engagement: {
          ...stored,
          coversActivity:
            stored.billingModel === 'tm' ||
            candidates.some((c) => c.engagementId === stored.engagementId),
        },
      };
    }
    // Stored but no longer active on this date (the entry moved, or the
    // engagement's term was amended out from under it) — fall through, so the
    // row is re-decided from the candidate count.
  }
  if (candidates.length === 0) return { kind: 'none' };
  if (candidates.length === 1) {
    // `candidates[0]` is present — length was just checked.
    return { kind: 'bound', engagement: candidates[0] as EngagementCandidate };
  }
  return { kind: 'ask', candidates };
}
